/**
 * useCaptureMessages — porta do listener global de mensagens da extensão
 * [application.js:15293]. A extensão (content.js na aba do painel) observa o
 * chrome.storage.onChanged e retransmite via postMessage:
 *
 *  - `{ type:'stop' }`            → sync.play virou FALSE (usuário parou, fechou a aba de
 *                                   captura, limite atingido ou popup da extensão) →
 *                                   changeCaptureButton(false): botão volta a "Começar a
 *                                   captura" e o form desbloqueia. [content.js:157]
 *  - `{ count, module_change }`   → sync.countX mudou durante a captura → updateCount:
 *                                   badge de "Ver leads" em tempo real (só se o módulo é o
 *                                   atual). [content.js:130-136]
 *  - `{ module_change }` sem count→ contador do módulo foi RESETADO (nova lista criada) →
 *                                   closeWindow() + loadModule(module): fecha a janela de
 *                                   captura e navega para as listas do módulo.
 *  - `{ close: true }`            → closeWindow() (popup da extensão pediu para fechar).
 *  - `{ capture: <module> }`      → showCategories() (popup da extensão pediu a captura).
 *
 * Montado uma vez no AppShell. Usa o subscribe do bridge (nunca window.addEventListener direto).
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { onExtensionMessage } from '../lib/extensionBridge';
import { useCaptureStore } from '../stores/captureStore';
import { LIST_MODULES, type ListModule } from '../config/constants';
import { requestListBackup } from '../lib/listBackupQueue';
import { getNewListIntent } from '../lib/newListIntent';
import { useSessionStore } from '../stores/sessionStore';
import { handleCaptureRuntimeEvent, pauseCaptureRuntime } from '../lib/captureRuntime';
import { isCaptureRuntimeEvent } from '../types/captureProtocol';

function isListModule(value: unknown): value is ListModule {
  return typeof value === 'string' && (LIST_MODULES as readonly string[]).includes(value);
}

export function useCaptureMessages(): void {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setLegacyActive = useCaptureStore((s) => s.setLegacyActive);
  const setCount = useCaptureStore((s) => s.setCount);
  const openCategories = useCaptureStore((s) => s.openCategories);

  useEffect(() => {
    const unsubscribe = onExtensionMessage((data) => {
      if (isCaptureRuntimeEvent(data)) {
        // O runtime serializa, compara o runId persistido e só projeta a pausa depois do commit.
        void handleCaptureRuntimeEvent(data).then((accepted) => {
          if (!accepted) return;
          void qc.invalidateQueries({ queryKey: ['me'] });
        });
        return;
      }

      // play → false em qualquer ponta (aba fechada, limite, popup, outro tab do painel).
      if (data['type'] === 'stop') {
        const capture = useCaptureStore.getState();
        const activeListId = capture.activeListId;
        if (capture.runId && activeListId) {
          // Compatibilidade D1: a extensão publicada ainda emite `stop` sem runId. A mensagem
          // continua válida, mas passa pela mesma autoridade durável; nunca altera a UI direto.
          void pauseCaptureRuntime(activeListId).then((accepted) => {
            if (accepted) void qc.invalidateQueries({ queryKey: ['me'] });
          });
          return;
        }
        setLegacyActive(false);
        if (activeListId) void requestListBackup(activeListId);
        void qc.invalidateQueries({ queryKey: ['me'] });
        return;
      }

      // Popup da extensão: abrir o fluxo de captura (legado: showCategories()).
      if (data['capture']) {
        openCategories();
        return;
      }

      // Popup da extensão: fechar a janela (legado: closeWindow()) — SÓ remove o
      // overlay; a tela de fundo fica como está.
      if (data['close'] === true) {
        useCaptureStore.getState().closeCaptureWindow();
        return;
      }

      const mod = data['module_change'];
      if (!isListModule(mod)) return;

      const count = data['count'];
      if (typeof count === 'number' && count >= 0) {
        const capture = useCaptureStore.getState();
        const userId = useSessionStore.getState().session?.userId ?? '';
        const pendingNewList = getNewListIntent(userId, mod) !== null;
        // O contador legado só identifica o módulo. Durante o fluxo local, isso não prova que
        // pertence à activeListId; a confirmação tipada do lote é a única fonte do badge.
        if (!capture.activeListId && mod === capture.module) {
          setCount(pendingNewList ? 0 : count, mod);
        }
        void qc.invalidateQueries({ queryKey: ['lists', mod] });
        void qc.invalidateQueries({ queryKey: ['me'] });
      } else {
        // Contador resetado (nova lista criada): legado fecha a janela E navega para as
        // listas do módulo (closeWindow + loadModule) — fluxo distinto do fechar manual.
        setCount(0, mod);
        void qc.invalidateQueries({ queryKey: ['lists', mod] });
        if (useCaptureStore.getState().captureWindow) {
          useCaptureStore.getState().closeCaptureWindow();
          navigate(`/${mod}`);
        }
      }
    });
    return unsubscribe;
  }, [qc, navigate, setLegacyActive, setCount, openCategories]);
}
